<template>
  <el-form v-loading="dialogBusy" ref="syncForm" :model="formModel" :rules="formRules" label-width="120px">
    <!-- Facility Selector -->
    <el-form-item label="Facility" prop="facId">
      <el-select v-model="formModel.facId" placeholder="Select a facility" @change="handleFacilityChange">
        <el-option v-for="facility in facilities" :key="facility.id" :label="facility.displayText" :value="facility.id"></el-option>
      </el-select>
      <span v-if="lastSync" class="last-sync">
        {{ lastSync }}
        <i v-if="dialogBusy" class="el-icon-loading"></i>
      </span>
    </el-form-item>

    <el-form-item label="Created Date" prop="createdDateTime">
      <el-date-picker v-model="formModel.createdDateTime" type="date" placeholder="Select date" :format="$configStore.datePickerDateFormat()" value-format="yyyy-MM-ddTHH:mm:ss" :picker-options="pickerOptions"></el-date-picker>
      <el-button data-testid="sync-date-info" size="mini" >
        <Icon :iconKey="'info'" :description="'Use this function to search PointClickCare for all records created on or after this date'" />
      </el-button>
    </el-form-item>
    <!-- Sync Button -->
    <div class="dialog-footer">
      <el-button data-testid="cancel-sync" @click="cancelForm" :disabled="dialogBusy">Cancel</el-button>
      <el-tooltip content="Immunization sync is only available once per day for each facility" :disabled="syncEnabled">
        <el-button data-testid="sync-facility-immunizations" type="primary" @click="syncImmunizationsForFacility" :disabled="dialogBusy || !syncEnabled">Sync</el-button>
      </el-tooltip>
    </div>
  </el-form>
</template>


<script>
import moment from "moment";
import Icon from "../../components/Shared/Icon.vue";
import { JOB_STATUS } from "../../services/pccSyncJobs";

// shown instead of the job's backend error text, which is internal and may say "PCC"
const SYNC_FAILED_MESSAGE = "Sync with PointClickCare failed. Try again later or contact support.";

export default {
  name: "SyncImmunizationsForFacility",
  components: {Icon},
  data() {
    return {
      facilities: [],
      dialogBusy: true,
      lastSync: "",
      syncEnabled: false,
      formModel: {
        facId: null,
        createdDateTime: null,
      },
      pollingInterval: null,
      syncEntityValues: {
        FACILITY_IMMUNIZATIONS: "facilityImmunizations",
      },
      pickerOptions: {
        disabledDate(input) {
          // disable dates after current date
          const currentDate = moment().startOf("day");
          const datepickerDate = moment(input).startOf("day");
          return datepickerDate.isAfter(currentDate);
        },
      },
      formRules: {
        facId: [{ required: true, type: "number", message: "Please select a facility", trigger: "change" }],
      },
    };
  },
  methods: {
    async fetchSyncStatus() {
      if (!this.formModel.facId) return;
      this.dialogBusy = true;
      try {
        const response = await this.$http.get(`${window.CONFIG.pcc_integration_api}/immunizations/facility/${this.formModel.facId}/sync-status`);
        const data = await response.json();
        this.lastSync = data.lastSync ? moment(data.lastSync).format(`${this.$configStore.dateFormat()} HH:mm`) : "Never";
        this.syncEnabled = data.syncEnabled;
        this.dialogBusy = false;
      } catch (error) {
        console.error("Error fetching sync status:", error);
        this.lastSync = "Error fetching sync status";
        this.syncEnabled = false;
        this.dialogBusy = false;
      }
    },
    async syncImmunizationsForFacility() {
      if (!this.syncEnabled) return;
      this.$refs.syncForm.validate(async (valid) => {
        if (valid) {
          this.dialogBusy = true;
          this.syncEnabled = false;
          this.lastSync = "Syncing...";
          try {
            const { facId, createdDateTime } = this.formModel;
            const body = {
              entity: this.syncEntityValues.FACILITY_IMMUNIZATIONS,
              ...(createdDateTime ? { createdDateTime: moment(createdDateTime).toISOString() } : {}),
            };
            const response = await this.$http.post(`${window.CONFIG.pcc_integration_api}/async-jobs/facility/${facId}`, body);
            const { jobId } = await response.json();

            this.startPollingJob(jobId);
          } catch (error) {
            this.lastSync = "Error";
            this.dialogBusy = false;
            console.error("Error during sync:", error);
          }
        } else {
          return false;
        }
      });
    },
    startPollingJob(jobId) {
      // clear any existing poller
      if (this.pollingInterval) clearInterval(this.pollingInterval);
      const interval = 5000;

      // start polling job status
      this.pollingInterval = setInterval(async () => {
        try {
          const res = await this.$http.get(`${window.CONFIG.pcc_integration_api}/async-jobs/job/${jobId}`);
          const data = await res.json();

          if (data.status === JOB_STATUS.COMPLETED) {
            this.lastSync = "Now";
            this.dialogBusy = false;
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
            this.$emit("immunization-sync-complete");
          } else if (data.status === JOB_STATUS.FAILED) {
            this.lastSync = SYNC_FAILED_MESSAGE;
            this.dialogBusy = false;
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
          } else {
            this.lastSync = "Syncing...";
            this.isSyncing = true;
          }
        } catch (err) {
          console.error("Polling error:", err);
          this.lastSync = "Error";
          this.dialogBusy = false;
          clearInterval(this.pollingInterval);
          this.pollingInterval = null;
        }
      }, interval);
    },
    cancelForm() {
      this.$refs.syncForm.resetFields();
      this.$emit("immunization-sync-close");
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    handleFacilityChange() {
      this.fetchSyncStatus();
    },
  },
  beforeDestroy() {
    if (this.pollingInterval) clearInterval(this.pollingInterval);
  },
  created() {
    const allSites = this.$configStore.data.sites || [];
    this.facilities = this.$configStore.pccIntegrationEnabled()
      ? allSites.filter(s => this.$configStore.isPccFacility(s.id))
      : allSites;
    this.dialogBusy = false;
  },
};
</script>

<style scoped>
.last-sync {
  display: block;
  font-size: 12px;
  color: gray;
}
</style>

